import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { BreederProfileLitterPropTypes } from 'dpl/shared/utils/customPropTypes';
import { isEqualFormValues } from 'dpl/shared/form/utils';
import { LITTER_STATES } from 'dpl/shared/constants/shared';
import TooltipWrapper from 'dpl/common/tooltip/components/TooltipWrapper';
import Icon from 'dpl/common/components/Icon';
import SmartImage from 'dpl/components/SmartImage';
import DogPlaceholderSvg from 'dpl/common/components/DogPlaceholderSvg';
import FullPageQuizQuestion from 'dpl/components/FullPageQuiz/FullPageQuizQuestion';

import puppyNoPreferencePng from 'dpl/assets/puppy_no_preference.png';

const LITTER_INPUT_NAME = 'litter_id';
const MAX_LITTERS_PER_ROW = 5;
const smartImageProps = {
  Tag: 'div',
  className: 'w-100 bg-center cover br1',
  crop: true,
  loadingClass: 'bg-light-gray',
  minHeight: '148px'
};

function QuizLitterQuestionOptionParentImage({ parent }) {
  return parent.profile_photo_url ? (
    <SmartImage
      {...smartImageProps}
      alt={parent.name}
      src={parent.profile_photo_url}
    />
  ) : (
    <div className="w-100 h-100 pv3 bg-light-gray br1">
      <DogPlaceholderSvg
        className="w-100 h-100 bg-light-gray br1"
        minHeight="124px"
      />
    </div>
  );
}

QuizLitterQuestionOptionParentImage.propTypes = {
  parent: PropTypes.shape({
    name: PropTypes.string,
    profile_photo_url: PropTypes.string
  }).isRequired
};

export function QuizLitterQuestionOption({
  isSelected,
  litter,
  inputName,
  onSelect
}) {
  const {
    id,
    status_and_date: statusAndDate,
    name: displayName,
    parents,
    puppies,
    sizes,
    status
  } = litter;
  const htmlId = `${inputName}-${id}`;
  const detailsString = `${sizes}${sizes && puppies.length ? ' \u2022 ' : ''}${
    puppies.length ? `${puppies.length} puppies` : ''
  }`;

  return (
    <label
      className={classnames(
        'AdoptionApplicationQuestionOption__label transition db bw1 br1',
        {
          'AdoptionApplicationQuestionOption__label--selected': isSelected
        }
      )}
      htmlFor={htmlId}
    >
      <input
        checked={isSelected}
        className="dn"
        id={htmlId}
        name={inputName}
        onClick={onSelect}
        onChange={() => null}
        type="radio"
        value={id}
      />
      <div className="f0 mb4">
        <div className="flex">
          {parents.map((parent, idx) => (
            <div
              className={classnames('w-50 b--white', {
                br: idx === 0,
                bl: idx === 1
              })}
              key={idx}
            >
              <QuizLitterQuestionOptionParentImage parent={parent} />
            </div>
          ))}
        </div>
      </div>
      <div className="ph4 pb4">
        <p className={classnames('mb1 fw-medium truncate')}>{displayName}</p>
        <div className="mb1 f2 mid-gray flex justify-center">
          <p
            className={classnames('truncate', {
              'mw-90': status === LITTER_STATES.EXPECTED
            })}
          >
            {statusAndDate}
          </p>
          {status === LITTER_STATES.EXPECTED && (
            <TooltipWrapper
              className="pl1 ba b--white"
              minWidth="300px"
              title={<span>Pregnancy confirmed</span>}
            >
              <Icon name="help-16" width="16px" height="16px" />
            </TooltipWrapper>
          )}
          {status === LITTER_STATES.PLANNED && (
            <TooltipWrapper
              className="pl1 ba b--white"
              minWidth="300px"
              title={<span>Pregnancy expected</span>}
            >
              <Icon name="help-16" width="16px" height="16px" />
            </TooltipWrapper>
          )}
        </div>

        {detailsString && (
          <p className="f2 mid-gray truncate">{detailsString}</p>
        )}
      </div>
    </label>
  );
}

QuizLitterQuestionOption.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  isSelected: PropTypes.bool.isRequired,
  litter: PropTypes.shape(BreederProfileLitterPropTypes).isRequired,
  inputName: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired
};

export default function QuizLitterQuestion({
  availableLitters,
  formState,
  handleNextClick,
  onChange
}) {
  const [litterDisplayCount, updateLitterDisplayCount] = useState(
    availableLitters.length > 9 ? 9 : availableLitters.length
  );
  const hasOneRowOnDesktop = availableLitters.length < MAX_LITTERS_PER_ROW - 1;

  return (
    <div className="AdoptionApplicationQuizLitterQuestion">
      <FullPageQuizQuestion
        handleNextClick={handleNextClick}
        title="Choose your litter preference"
        subtitleClassName="mw-50-md center"
        variant="wide"
        isValid
      >
        <ul
          className={classnames('row', {
            'justify-center-l': hasOneRowOnDesktop
          })}
        >
          {availableLitters.map((litter, idx) => {
            const leftPadding =
              !hasOneRowOnDesktop && idx % MAX_LITTERS_PER_ROW === 0;
            return (
              <li
                className={classnames('mb6 col-xl-2 col-lg-3 col-md-4 col-6', {
                  'offset-xl-1': leftPadding,
                  dn: idx >= litterDisplayCount
                })}
                key={idx}
              >
                <QuizLitterQuestionOption
                  key={`litter-${litter.id}`}
                  isSelected={isEqualFormValues(
                    formState[LITTER_INPUT_NAME],
                    litter.id
                  )}
                  litter={litter}
                  inputName="litter_preference"
                  onSelect={() => onChange('litter_id', litter.id)}
                />
              </li>
            );
          })}
          <li
            className={classnames('mb6 col-xl-2 col-lg-3 col-md-4 col-6', {
              'offset-xl-1': litterDisplayCount % 5 === 0
            })}
          >
            <label
              className={classnames(
                'AdoptionApplicationQuestionOption__label transition db bw1 br1',
                {
                  'AdoptionApplicationQuestionOption__label--selected':
                    !formState[LITTER_INPUT_NAME]
                }
              )}
              htmlFor="litter_preference-"
            >
              <input
                checked={!formState[LITTER_INPUT_NAME]}
                className="dn"
                id="litter_preference-"
                name={LITTER_INPUT_NAME}
                onClick={() => onChange('litter_id', '')}
                onChange={() => null}
                type="radio"
                value=""
              />
              <div className="f0 mb4">
                <div className="flex">
                  <SmartImage
                    {...smartImageProps}
                    Tag="div"
                    src={puppyNoPreferencePng}
                  />
                </div>
              </div>
              <div className="ph4 pb4">
                <p className={classnames('mb1 fw-medium')}>No preference</p>
                <p className="lh-copy f2 mid-gray ws-normal">
                  Work with your breeder to get matched
                </p>
              </div>
            </label>
          </li>
        </ul>
        <div>
          {litterDisplayCount < availableLitters.length && (
            <button
              type="button"
              className="purple"
              onClick={() => updateLitterDisplayCount(availableLitters.length)}
            >
              Show More
            </button>
          )}
        </div>
      </FullPageQuizQuestion>
    </div>
  );
}

QuizLitterQuestion.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  formState: PropTypes.object.isRequired,
  availableLitters: PropTypes.arrayOf(
    PropTypes.shape(BreederProfileLitterPropTypes)
  ).isRequired,
  handleNextClick: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};
